/* eslint-disable
	@typescript-eslint/no-unsafe-call,
	@typescript-eslint/no-unsafe-argument,
	@typescript-eslint/no-unsafe-member-access,
	@typescript-eslint/no-unsafe-assignment
*/
import axios, { type AxiosRequestConfig } from "axios";
import parse from "url-parse";
import I18Next from "./Control/Vue/I18Next";
import { type ForNotifier } from "./Control/Vue/Notifications/Shared/DerivedInterfaces";
import { Notifier } from "./Control/Vue/Notifications/Shared/Notifier";
import { MiaPlaza } from "./Reinforced.Typings";

declare module "axios" {
	interface AxiosRequestConfig {
		/**
		 * When `true`, failed requests do not show the global error toast or trigger a 503 redirect.
		 * Use only when the caller handles errors locally.
		 */
		skipDefaultErrorHandler?: boolean
		/**
		 * When `true`, the request bypasses the shared concurrency limiter (if enabled).
		 * Use sparingly for requests that must not wait behind the queue.
		 */
		skipConcurrencyLimiter?: boolean
	}
}

let hasShownDegradedNotification = false;

export function setup(): void {
	setupNotificationOnError();
	setupPostLongQueries();
}

function setupNotificationOnError(): void {
	// Register an error handler for all Axios requests
	axios.interceptors.response.use(
		(response) => response,
		(error) => {
			// do not show error message popup if the request has been canceled.
			if (axios.isCancel(error)) {
				throw error;
			}

			if (error.response?.data?.degraded === true) {
				const message = error.response.data?.message;

				// only show the notification if the message is not null/undefined
				if (message != null && message !== "") {
					if (!hasShownDegradedNotification) {
						Notifier.show<MiaPlaza.Control.Vue.Notifications.ISessionMessage>({
							Message: message,
							Template: "SessionMessage",
							Type: MiaPlaza.Control.Vue.Notifications.SessionMessageType.Info,
							Timeout: 0,
						});
					}

					hasShownDegradedNotification = true;
				}

				const hangIndefinitely = new Promise<never>(() => {
					// Intentionally empty: degraded requests must never resolve or reject.
				});

				// Returned to callers for degraded responses so they never settle.
				return hangIndefinitely;
			}

			if (error.config?.skipDefaultErrorHandler === true) {
				throw error;
			}

			const showErrorMessage = (message: string) => {
				const notification: ForNotifier<MiaPlaza.Control.Vue.Notifications.ISessionMessage> = {
					Message: message,
					Template: "SessionMessage",
					Type: MiaPlaza.Control.Vue.Notifications.SessionMessageType.Error,
				};
				Notifier.show(notification);
			};

			if (error.response?.data?.message != null) {
				showErrorMessage(error.response.data.message);
			}
			else {
				void I18Next.tr("ErrorUnexpectedAnswerFromServer").then(showErrorMessage);
			}

			if (error.response?.status === 503 && error.response?.data?.location) {
				window.location = error.response.data.location;
			}

			// It's the responsibility of the server-logging to break infinite recursions here, i.e.,
			// they should disable all error handling when posting the error with axios to the server.
			throw new Error(error.message);
		},
	);
}

function setupPostLongQueries() {
	// Rewrite GET requests that would exceed typical URL size limits to POST requests
	// (our newer server code, i.e., the Routing framework can handle this)
	axios.interceptors.request.use(
		(request) => {
			if (request.method === "get") {
				// All browsers can handle URLs with 2000 characters: https://stackoverflow.com/a/417184/812379.
				if (request.url != null && request.url.length > 2000) {
					rewriteToPost(request);
				}
			}
			return request;
		}, (error) => {
			return Promise.reject(error);
		},
	);
}

function rewriteToPost(request: AxiosRequestConfig) {
	request.method = "post";
	request.data = request.data || new FormData();

	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const parsed = parse(request.url!, true);
	const query = parsed.query;
	parsed.set("query", {});

	for (const [key, value] of Object.entries(query)) {
		if (value == null || value.length < 32) {
			// We keep short bits in the query string, honestly, so that the _= marker parameter
			// is not lost. But also because this makes things possibly a bit more readable.
			parsed.query[key] = value;
		}
		else {
			parsed.query[key] = MiaPlaza.HttpPipeline.Middlewares.RoutingMiddleware.POST_DATA_MARKER;
			request.data.append(key, value);
		}
	}

	request.url = parsed.href;
	request.params = parsed.query;
}
